import PropTypes from "prop-types";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {PrimaryButton, SecondaryButton} from "@/components/Button";
import usePagoMovilRegistration from "../../../hooks/usePagoMovilRegistration";
import useResendEmailVerification from "../../../hooks/useResendEmailVerification";
import useWithdrawalPagoMovil from "../../../hooks/wallet/useWithdrawal.pagoMovil.hook";
import {useBalanceStore} from "../../../store/useBalance.store";
import CustomSteps from "../../CustomSteps";
import SelectAccount from "./components/SelectAccount";
import WithdrawalAmount from "./components/WithdrawalAmount";
import WithdrawalConfirm from "./components/WithdrawalConfirm";
import WithdrawalFailure from "./components/WithdrawalFailure";
import WithdrawalSuccess from "./components/WithdrawalSuccess";
import styles from "./WithdrawalSteps.module.css";

const WithdrawalSteps = ({onBack, onCancel}) => {
  const [current, setCurrent] = useState(0);
  const [selectBankAccount, setSelectBankAccount] = useState({});
  const [withdrawalAmount, setWithdrawalAmount] = useState(0);
  const [isNextDisabled, setIsNextDisabled] = useState(true);

  // Result states for success/failure views
  const [withdrawalState, setWithdrawalState] = useState("form"); // "form" | "success" | "failure"
  const [failureErrorMessage, setFailureErrorMessage] = useState("");
  const [feedback, setFeedback] = useState(null);
  const {requestWithdrawal, isPending, isError, isSuccess, error} = useWithdrawalPagoMovil();
  const {triggerRegistrationModalIfNeeded} = usePagoMovilRegistration();
  const {resendVerification, isSuccess: isEmailSent, isError: isEmailError} = useResendEmailVerification();

  const {handleWithdrawalModal} = useBalanceStore();

  const {t} = useTranslation();

  const steps = [
    {
      title: t("withdrawalStepsAccount"),
      content: (
        <SelectAccount
          setSelectBankAccount={setSelectBankAccount}
          setIsNextDisabled={setIsNextDisabled}
        />
      ),
    },
    {
      title: t("withdrawalStepsAmount"),
      content: (
        <WithdrawalAmount
          setWithdrawalAmount={setWithdrawalAmount}
          setIsNextDisabled={setIsNextDisabled}
        />
      ),
    },
    {
      title: t("withdrawalStepsWidthdraw"),
      content: (
        <WithdrawalConfirm
          selectBankAccount={selectBankAccount}
          withdrawalAmount={withdrawalAmount}
        />
      ),
    },
  ];

  const resetWithdrawalState = () => {
    setWithdrawalAmount(0);
    setSelectBankAccount({});
    setCurrent(0);
    setWithdrawalState("form");
    setFailureErrorMessage("");
  };

  // Helper to go back to form for retry (keeps the amount and account selected)
  const handleRetry = () => {
    setWithdrawalState("form");
    setFailureErrorMessage("");
  };

  const handleCloseModal = () => {
    resetWithdrawalState();
    handleWithdrawalModal();
  };

  const next = () => {
    setCurrent(current + 1);
  };

  const prev = () => {
    setCurrent(current - 1);
  };

  const handleConfirm = async () => {
    // Check if user needs pago móvil registration before proceeding
    const registrationResult = await triggerRegistrationModalIfNeeded("withdrawal");

    if (registrationResult) {
      setFeedback({type: "warning", message: t("withdrawalExplanationMessage")});

      // Close the withdrawal modal since registration modal will open
      setTimeout(() => {
        resetWithdrawalState();
        handleWithdrawalModal();
      }, 1500);

      return;
    }

    requestWithdrawal({
      amount: Number(withdrawalAmount),
      pm_account_id: selectBankAccount?.id,
    });
  };

  // Helper function to get error message from axios error or regular error
  const getErrorMessage = err => {
    return err?.response?.data?.message || err?.message || "";
  };

  // Helper function to get user-friendly error description
  const getErrorDescription = errorMessage => {
    if (errorMessage.includes("minWageNotReached")) {
      return t("withdrawalStepsErrorDesc_1");
    }
    if (errorMessage.includes("emailNotVerified")) {
      return t("withdrawalStepsErrorDesc_3");
    }
    if (errorMessage.includes("minMaxNotReached")) {
      return t("withdrawalStepsErrorDesc_4");
    }
    return t("withdrawalStepsErrorDesc_2");
  };

  useEffect(() => {
    if (isError) {
      const errorMessage = getErrorMessage(error);
      const errorDescription = getErrorDescription(errorMessage);

      // If email not verified, automatically resend verification email
      if (errorMessage.includes("emailNotVerified")) {
        resendVerification();
      }

      // Set failure state with error message
      setFailureErrorMessage(errorDescription);
      setWithdrawalState("failure");
    } else if (isSuccess) {
      // Set success state
      setWithdrawalState("success");
    }
  }, [isError, isSuccess, error, t]);

  // Handle email verification success/error feedback
  useEffect(() => {
    if (isEmailSent) {
      setFeedback({type: "success", message: t("emailVerificationSentMessage")});
    }
    if (isEmailError) {
      setFeedback({type: "error", message: t("emailVerificationErrorMessage")});
    }
  }, [isEmailSent, isEmailError, t]);

  // Auto-dismiss feedback after 5s
  useEffect(() => {
    if (!feedback) return;
    const timer = setTimeout(() => setFeedback(null), 5000);
    return () => clearTimeout(timer);
  }, [feedback]);

  // Render success state
  if (withdrawalState === "success") {
    return (
      <div className={styles.resultContainer}>
        <WithdrawalSuccess
          amount={withdrawalAmount}
          onClose={handleCloseModal}
        />
      </div>
    );
  }

  // Render failure state
  if (withdrawalState === "failure") {
    return (
      <div className={styles.resultContainer}>
        <WithdrawalFailure
          errorMessage={failureErrorMessage}
          onRetry={handleRetry}
          onClose={handleCloseModal}
        />
      </div>
    );
  }

  return (
    <div className={styles.stepsContainer}>
      <CustomSteps
        steps={steps}
        current={current}
      />
      {feedback && (
        <div className={`${styles.feedbackBanner} ${styles[feedback.type]}`}>
          <span>{feedback.message}</span>
          <button
            type="button"
            className={styles.feedbackClose}
            onClick={() => setFeedback(null)}
          >
            ✕
          </button>
        </div>
      )}
      <div className={styles.stepContent}>{steps[current].content}</div>
      <div className={`${styles.actionsBar} ${current === 0 ? styles.centered : ""}`}>
        {current < steps.length - 1 && (
          <PrimaryButton
            onClick={() => next()}
            disabled={isNextDisabled}
          >
            {t("withdrawalStepsNext")}
          </PrimaryButton>
        )}
        {current === steps.length - 1 && (
          <PrimaryButton
            disabled={isPending}
            onClick={() => handleConfirm()}
          >
            {t("withdrawalStepsConfirm")}
          </PrimaryButton>
        )}
        <SecondaryButton
          onClick={() => {
            if (current > 0) {
              prev();
            } else if (typeof onBack === "function") {
              resetWithdrawalState();
              onBack();
            }
          }}
        >
          {t("withdrawalStepsBack")}
        </SecondaryButton>
        <SecondaryButton
          onClick={() => {
            resetWithdrawalState();
            if (typeof onCancel === "function") onCancel();
          }}
        >
          {t("withdrawalStepsCancel")}
        </SecondaryButton>
      </div>
    </div>
  );
};
export default WithdrawalSteps;

WithdrawalSteps.propTypes = {
  onBack: PropTypes.func,
  onCancel: PropTypes.func,
};
